import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  startAssignFlowCoverageMember,
  startDeleteFlowCoverageMember,
  startLoadingFlowCoverage,
  startReorderFlowCoverageMembers,
  startUpdateFlowCoverageMember
} from "../../app/actions/flowCoverageActions";

const formatUser = (user) => {
  if (!user) {
    return "";
  }
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  if (user.user_name && fullName) {
    return `${user.user_name} — ${fullName}`;
  }
  return user.user_name || fullName || user.email || String(user.id);
};

const restoreWindowScroll = (top) => {
  if (top == null) {
    return;
  }
  const html = document.documentElement;
  const previous = html.style.scrollBehavior;
  html.style.scrollBehavior = "auto";
  const apply = () => window.scrollTo({ top, left: 0, behavior: "instant" });
  apply();
  window.requestAnimationFrame(() => {
    apply();
    window.requestAnimationFrame(() => {
      apply();
      html.style.scrollBehavior = previous;
    });
  });
};

const FlowCoverageWorkAreaPanel = ({ catalogmoduleId, roleKey }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading, axis, segmenting, groups, eligibleUsers } = useSelector((state) => state.flowCoverage);
  const [assignUserId, setAssignUserId] = useState("");
  const pendingScrollY = useRef(null);
  const hasGroups = (groups || []).length > 0;

  useEffect(() => {
    if (!catalogmoduleId || !roleKey) {
      return;
    }
    dispatch(startLoadingFlowCoverage({
      catalogmodule_id: catalogmoduleId,
      role: roleKey
    }));
    setAssignUserId("");
  }, [catalogmoduleId, dispatch, roleKey]);

  const usersById = useMemo(() => {
    const map = new Map();
    (eligibleUsers || []).forEach((user) => {
      map.set(Number(user.id), user);
    });
    return map;
  }, [eligibleUsers]);

  const axisLabel = useMemo(() => {
    if (axis === "ciiu_l1") {
      return t("admin.flowModule.flowCoverage.axis.ciiu");
    }
    if (axis === "problem") {
      return t("admin.flowModule.flowCoverage.axis.problem");
    }
    if (axis === "organism") {
      return t("admin.flowModule.flowCoverage.axis.organism");
    }
    if (axis === "operation_size") {
      return t("admin.flowModule.flowCoverage.axis.operationSize");
    }
    if (axis === "topic") {
      return t("admin.flowModule.flowCoverage.axis.topic");
    }
    return axis ? t("admin.flowModule.flowCoverage.axis.none") : "";
  }, [axis, t]);

  const resolveMemberUser = (member) => (
    member.User_To_FlowCoverageMember
    || usersById.get(Number(member.user_id))
    || { id: member.user_id }
  );

  const reload = (silent = false) => {
    dispatch(startLoadingFlowCoverage({
      catalogmodule_id: catalogmoduleId,
      role: roleKey
    }, { silent }));
  };

  const isUserInGroup = (group) => {
    const members = group.FlowCoverageMember_To_FlowCoverageGroup || [];
    return members.some((member) => !member.deleted && Number(member.user_id) === Number(assignUserId));
  };

  const handleAssign = async (group) => {
    if (!assignUserId) {
      return;
    }
    if (isUserInGroup(group)) {
      Swal.fire(
        t("titles.info", t("titles.attention", "Aviso")),
        t("admin.flowModule.flowCoverage.alreadyInGroup"),
        "info"
      );
      return;
    }
    const saved = await dispatch(startAssignFlowCoverageMember({
      flow_coverage_group_id: Number(group.id),
      user_id: Number(assignUserId)
    }));
    if (saved) {
      setAssignUserId("");
      pendingScrollY.current = window.scrollY;
      reload(true);
    }
  };

  useEffect(() => {
    if (loading || pendingScrollY.current == null) {
      return;
    }
    const top = pendingScrollY.current;
    pendingScrollY.current = null;
    restoreWindowScroll(top);
  }, [loading, groups]);

  const isMemberActive = (member) => member.active !== false;

  const handleToggleActive = (member, nextActive) => {
    dispatch(startUpdateFlowCoverageMember(member.id, { active: nextActive }));
  };

  const handleRemove = async (memberId) => {
    pendingScrollY.current = window.scrollY;
    const ok = await dispatch(startDeleteFlowCoverageMember(memberId));
    if (ok) {
      reload(true);
    } else {
      pendingScrollY.current = null;
    }
  };

  const handleMove = async (group, memberIndex, delta) => {
    const members = [...(group.FlowCoverageMember_To_FlowCoverageGroup || [])]
      .sort((left, right) => Number(left.sequential_order || 0) - Number(right.sequential_order || 0));
    const nextIndex = memberIndex + delta;
    if (nextIndex < 0 || nextIndex >= members.length) {
      return;
    }
    const swapped = [...members];
    const [moved] = swapped.splice(memberIndex, 1);
    swapped.splice(nextIndex, 0, moved);
    pendingScrollY.current = window.scrollY;
    const ok = await dispatch(startReorderFlowCoverageMembers(group.id, swapped.map((item) => item.id)));
    if (ok) {
      reload(true);
    } else {
      pendingScrollY.current = null;
    }
  };

  if (!catalogmoduleId) {
    return (
      <div className="alert alert-warning">
        {t("admin.flowModule.flowCoverage.selectModule")}
      </div>
    );
  }

  return (
    <div className="mb-4" style={{ overflowAnchor: "none" }}>
      <h5 className="mb-2">{t("admin.flowModule.flowCoverage.title")}</h5>
      {axisLabel ? <p className="text-muted small">{axisLabel}</p> : null}
      {loading && !hasGroups && <p className="text-muted">{t("general.loading", "Cargando...")}</p>}

      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-5">
          <label className="form-label">{t("admin.flowModule.flowCoverage.addPerson")}</label>
          <select className="form-select" value={assignUserId} onChange={(event) => setAssignUserId(event.target.value)}>
            <option value="">{t("admin.flowModule.flowCoverage.selectUser")}</option>
            {(eligibleUsers || []).map((user) => (
              <option key={user.id} value={user.id}>{formatUser(user)}</option>
            ))}
          </select>
        </div>
      </div>

      {(groups || []).map((group) => {
        const members = [...(group.FlowCoverageMember_To_FlowCoverageGroup || [])]
          .sort((left, right) => Number(left.sequential_order || 0) - Number(right.sequential_order || 0));
        const areas = Array.isArray(group.area_keys) ? group.area_keys.join(", ") : String(group.area_keys || "");
        return (
          <div className="card mb-3" key={group.id}>
            <div className="card-header d-flex flex-wrap justify-content-between gap-2">
              <strong>{group.name || group.code}</strong>
              <span className="small">
                {segmenting
                  ? `${t("admin.flowModule.flowCoverage.areas")}: ${areas || "—"}`
                  : t("admin.flowModule.flowCoverage.transversalPack")}
              </span>
            </div>
            <div className="card-body p-0">
              <div className="p-2 border-bottom">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={!assignUserId || isUserInGroup(group)}
                  onClick={() => handleAssign(group)}
                >
                    {t("admin.flowModule.flowCoverage.assignHere")}
                </button>
              </div>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>{t("admin.flowModule.flowCoverage.order")}</th>
                    <th>{t("admin.flowModule.flowCoverage.person")}</th>
                    <th>{t("admin.flowModule.flowCoverage.active")}</th>
                    <th>{t("admin.flowModule.flowCoverage.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member, index) => (
                    <tr
                      key={member.id}
                      className={isMemberActive(member) ? "" : "table-secondary text-muted"}
                    >
                      <td>{member.sequential_order}</td>
                      <td>{formatUser(resolveMemberUser(member))}</td>
                      <td>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={`member-active-${member.id}`}
                            checked={isMemberActive(member)}
                            tabIndex={-1}
                            onMouseDown={(event) => event.preventDefault()}
                            onFocus={(event) => event.currentTarget.blur()}
                            onChange={(event) => handleToggleActive(member, event.target.checked)}
                            aria-label={t("admin.flowModule.flowCoverage.active")}
                          />
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          disabled={index === 0}
                          onClick={() => handleMove(group, index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-2"
                          disabled={index === members.length - 1}
                          onClick={() => handleMove(group, index, 1)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={() => handleRemove(member.id)}
                        >
                          {t("buttons.delete", "Quitar")}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!members.length && (
                    <tr>
                      <td colSpan={4} className="text-muted">
                        {t("admin.flowModule.flowCoverage.emptyGroup")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

FlowCoverageWorkAreaPanel.propTypes = {
  catalogmoduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  roleKey: PropTypes.string.isRequired
};

export default FlowCoverageWorkAreaPanel;
